interface SurveyQuestion {
  id: number
  text: string
  type: string
  isRequired?: boolean
  maxSelections?: number
  modelPath?: string
  component?: string
  responses?: Array<{
    id: number
    text: string
    nestedQuestions?: SurveyQuestion[]
  }>
  hideText?: boolean
}

interface SurveyQuestionResponse {
  id?: number
  surveyQuestionId: number
  surveyQuestionResponseId?: number
  responseValue?: string | null
  metadata?: Record<string, any>
  hideText?: boolean
}

/**
 * Checks if a question should be shown based on conditional logic
 * @param question - The question to check
 * @param allAnswers - All answers from the Zustand store (arrays of responses)
 * @param isGuestPass - Whether the client is a guestpass client
 * @returns true if the question should be shown, false if it should be skipped
 */
export const shouldShowQuestion = (
  question: SurveyQuestion,
  allAnswers: Record<string, SurveyQuestionResponse[]>,
  isGuestPass?: boolean
): boolean => {
  const questionId = Number(question.id)

  // Condition 1: Question 229 - only show if client is guestpass
  if (questionId === 229) {
    return isGuestPass === true
  }

  // Condition 2: Payer question (ID 1586) - show memberId question (20250315) only if memberIdRequired is true
  if (questionId === 20250315) {
    const payerQuestionId = '1586'
    const payerResponses = allAnswers[payerQuestionId]
    const mostRecentPayerResponse = payerResponses?.[payerResponses.length - 1]
    const memberIdRequired = mostRecentPayerResponse?.metadata?.memberIdRequired

    // Only show question 20250315 if payer question has memberIdRequired: true
    return memberIdRequired === true
  }

  // Condition 3: Question 207 - only show if question 219 has surveyQuestionResponseId = 4
  if (questionId === 207) {
    const question219Id = '219'
    const question219Responses = allAnswers[question219Id]

    // Check if any response in the array has surveyQuestionResponseId = 4
    if (!question219Responses || question219Responses.length === 0) {
      return false
    }

    const hasResponseId4 = question219Responses.some(
      response => response.surveyQuestionResponseId === 4
    )

    // Only show question 207 if question 219 has at least one response with surveyQuestionResponseId = 4
    return hasResponseId4
  }

  // Condition 4: Question 400 - only show if question 219 has responses
  if (questionId === 400) {
    const question219Id = '219'
    const question219Responses = allAnswers[question219Id]

    // Skip question 400 if question 219 has no responses
    if (!question219Responses || question219Responses.length === 0) {
      return false
    }

    // Check if there are any valid response IDs from question 219
    const hasValidResponses = question219Responses.some(
      response => response.surveyQuestionResponseId != null
    )

    // Only show question 400 if question 219 has at least one valid response
    return hasValidResponses
  }

  // Default: show all questions that don't have specific conditions
  return true
}

/**
 * Filters questions based on conditional logic
 * @param questions - Array of all questions
 * @param allAnswers - All answers from the Zustand store (arrays of responses)
 * @param isGuestPass - Whether the client is a guestpass client
 * @returns Filtered array of questions that should be shown
 */
export const filterQuestions = (
  questions: SurveyQuestion[],
  allAnswers: Record<string, SurveyQuestionResponse[]>,
  isGuestPass?: boolean
): SurveyQuestion[] => {
  return questions.filter(question =>
    shouldShowQuestion(question, allAnswers, isGuestPass)
  )
}
